import { apiRequest, PlayerApiError } from '@/api/client';
import { flushWarnings, pushWarning } from '@/lib/warningsBuffer';

export type Connectivity = 'online' | 'degraded' | 'offline';

interface HeartbeatRuntime {
  playerVersion: string;
  getCurrentItemId(): string | null;
  getConnectivity(): Connectivity;
  onAck?(at: Date): void;
}

const HEARTBEAT_INTERVAL_MS = 60_000;
const JITTER_MS = 5_000;
const MAX_BACKOFF_MS = 5 * 60_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let backoff = HEARTBEAT_INTERVAL_MS;
let lastNetworkOkAt: Date | null = null;

interface MemoryPerf {
  usedJSHeapSize?: number;
}

function readMemory(): number | undefined {
  const m = (performance as unknown as { memory?: MemoryPerf }).memory;
  if (m && typeof m.usedJSHeapSize === 'number') return m.usedJSHeapSize;
  return undefined;
}

function jittered(base: number): number {
  return base + Math.floor((Math.random() * 2 - 1) * JITTER_MS);
}

async function sendHeartbeat(rt: HeartbeatRuntime): Promise<void> {
  const memory = readMemory();
  const payload: Record<string, unknown> = {
    timestamp: new Date().toISOString(),
    playerVersion: rt.playerVersion,
    connectivity: rt.getConnectivity(),
    screen: {
      widthPx: window.innerWidth,
      heightPx: window.innerHeight,
    },
    warnings: flushWarnings(),
  };
  const item = rt.getCurrentItemId();
  if (item) payload.lastPlayedItemId = item;
  if (memory !== undefined) payload.memoryUsedBytes = memory;
  if (lastNetworkOkAt) payload.lastNetworkOkAt = lastNetworkOkAt.toISOString();

  try {
    await apiRequest<{ ok: true }>('/devices/heartbeat', {
      method: 'POST',
      body: payload,
    });
    const now = new Date();
    lastNetworkOkAt = now;
    rt.onAck?.(now);
    backoff = HEARTBEAT_INTERVAL_MS;
  } catch (err) {
    // Re-buffer warnings we just flushed so they can retry next cycle.
    if (Array.isArray(payload.warnings)) {
      for (const w of payload.warnings as string[]) pushWarning(w);
    }
    if (err instanceof PlayerApiError && err.status >= 500) {
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    } else if (err instanceof PlayerApiError && err.status === 401) {
      // Refresh handled inside apiRequest; the next tick will retry.
      backoff = HEARTBEAT_INTERVAL_MS;
    } else {
      // Network failure
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    }
  }
}

export function startHeartbeat(rt: HeartbeatRuntime): () => void {
  const tick = (): void => {
    void sendHeartbeat(rt).finally(() => {
      timer = setTimeout(tick, jittered(backoff));
    });
  };
  // First heartbeat fires after a short initial delay so the player has had a
  // chance to load a segment.
  timer = setTimeout(tick, 5_000);
  return () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
}

export function getLastNetworkOkAt(): Date | null {
  return lastNetworkOkAt;
}
